import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import { StyledComponentProps as _StyledComponentProps } from "styled-components";
import _styled from "styled-components";
var _excluded = ["appearance", "dark", "size", "widthWithTouchArea", "heightWithTouchArea", "shape", "elevation", "background", "disabled", "Icon", "iconSize", "children"],
    _excluded2 = ["background", "reaction", "width", "height", "appearance", "dark", "elevation"];
import { colors } from "@wantedly_private/ui-foundation";
import { DynamicIcon } from "@wantedly_private/ui-icons";
import { foundationCss, useDevice, useMergeRefs, useReactionCss, useReactionWithTouchArea } from "@wantedly_private/ui-react-util";
import React from "react";
var PADDING = 4;

/**
 * @deprecated In favor of `createIconButton` from "@wantedly_private/ui-react-2022"
 * Design: https://www.figma.com/file/oTPln5V0gbCuVLZ0EUG3pU/UIComponentsV2?node-id=2898%3A4346
 *
 * An Icon wrapped in a button component.
 */
export var DynamicIconButton = /*#__PURE__*/React.forwardRef(function IconButton(_ref, forwardedRef) {
  var _ref$appearance = _ref.appearance,
      appearance = _ref$appearance === void 0 ? "colored" : _ref$appearance,
      _ref$dark = _ref.dark,
      dark = _ref$dark === void 0 ? false : _ref$dark,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? "medium" : _ref$size,
      touchAreaWidthOverride = _ref.widthWithTouchArea,
      touchAreaHeightOverride = _ref.heightWithTouchArea,
      _ref$shape = _ref.shape,
      shape = _ref$shape === void 0 ? "R100" : _ref$shape,
      _ref$elevation = _ref.elevation,
      elevation = _ref$elevation === void 0 ? 4 : _ref$elevation,
      _ref$background = _ref.background,
      background = _ref$background === void 0 ? colors.blue400 : _ref$background,
      _ref$disabled = _ref.disabled,
      disabled = _ref$disabled === void 0 ? false : _ref$disabled,
      Icon = _ref.Icon,
      iconSize = _ref.iconSize,
      _children = _ref.children,
      restProps = _objectWithoutProperties(_ref, _excluded);

  var _useReactionWithTouch = useReactionWithTouchArea({
    hoverable: true,
    pressable: true,
    focusable: true,
    disabled: disabled,
    touchArea: {
      touch: "click",
      disabled: disabled,
      paddingVertical: PADDING,
      paddingHorizontal: PADDING
    }
  }),
      TouchArea = _useReactionWithTouch.TouchArea,
      touchAreaProps = _useReactionWithTouch.touchAreaProps,
      targetRef = _useReactionWithTouch.targetRef,
      reaction = _useReactionWithTouch.reaction,
      targetProps = _useReactionWithTouch.targetProps;

  var ref = useMergeRefs(forwardedRef, targetRef); // In this component, `height` should be identical to `width`

  var touchAreaDefaultSize = typeof size === "number" ? size : sizeMap[size];
  var touchAreaWidth = touchAreaWidthOverride !== null && touchAreaWidthOverride !== void 0 ? touchAreaWidthOverride : touchAreaDefaultSize;
  var touchAreaHeight = touchAreaHeightOverride !== null && touchAreaHeightOverride !== void 0 ? touchAreaHeightOverride : touchAreaDefaultSize;
  var buttonWidth = touchAreaWidth - 2 * PADDING;
  var buttonHeight = touchAreaHeight - 2 * PADDING;
  var iconWidth = iconSizeMap[iconSize !== null && iconSize !== void 0 ? iconSize : buttonWidth < 48 || buttonHeight < 48 ? "small" : "large"];
  return /*#__PURE__*/React.createElement(TouchArea, touchAreaProps, /*#__PURE__*/React.createElement(SolidIconButton, _extends({}, targetProps, {
    reaction: reaction,
    appearance: appearance,
    dark: dark,
    shape: shape,
    elevation: elevation,
    background: background,
    disabled: disabled,
    width: buttonWidth,
    height: buttonHeight,
    ref: ref
  }, restProps), /*#__PURE__*/React.createElement(StyledIcon, {
    Icon: Icon,
    width: iconWidth,
    height: iconWidth
  })));
});
var backgroundMap = {
  colored: {
    light: {
      abled: "transparent",
      // Do not this color, and Use background prop.
      disabled: colors.white
    },
    dark: {
      abled: colors.white,
      disabled: "transparent"
    }
  },
  base: {
    light: {
      abled: colors.white,
      disabled: colors.white
    },
    dark: {
      abled: "transparent",
      disabled: "transparent"
    }
  },
  clear: {
    light: {
      abled: "transparent",
      disabled: "transparent"
    },
    dark: {
      abled: "transparent",
      disabled: "transparent"
    }
  }
};
var overlayTypeMap = {
  colored: {
    light: {
      abled: "white",
      disabled: "black"
    },
    dark: {
      abled: "black",
      disabled: "white"
    }
  },
  base: {
    light: {
      abled: "black",
      disabled: "black"
    },
    dark: {
      abled: "white",
      disabled: "white"
    }
  },
  clear: {
    light: {
      abled: "black",
      disabled: "black"
    },
    dark: {
      abled: "white",
      disabled: "white"
    }
  }
};
var SolidIconButton = /*#__PURE__*/React.forwardRef(function SolidButton(_ref2, ref) {
  var background = _ref2.background,
      reaction = _ref2.reaction,
      width = _ref2.width,
      height = _ref2.height,
      appearance = _ref2.appearance,
      dark = _ref2.dark,
      elevation = _ref2.elevation,
      props = _objectWithoutProperties(_ref2, _excluded2);

  var device = useDevice();

  var _useReactionCss = useReactionCss({
    reactionType: "colorAndElevation",
    reaction: reaction,
    elevation: elevation,
    overlayType: overlayTypeMap[appearance][dark ? "dark" : "light"][props.disabled ? "disabled" : "abled"],
    background: appearance === "colored" && !dark && !props.disabled ? background : backgroundMap[appearance][dark ? "dark" : "light"][props.disabled ? "disabled" : "abled"]
  }),
      boxShadow = _useReactionCss.boxShadow,
      backgroundImage = _useReactionCss.backgroundImage;

  return /*#__PURE__*/React.createElement(StyledSolidButton, _extends({
    ref: ref,
    width: width,
    height: height,
    textStyle: null,
    reaction: {
      boxShadow: appearance === "clear" ? "none" : appearance === "base" && dark ? "inset 0 0 0 1px ".concat(colors.whiteAlpha200) : boxShadow,
      backgroundImage: appearance === "clear" ? "transparent" : backgroundImage
    },
    appearance: appearance,
    dark: dark,
    reactionType: reaction,
    elevation: appearance === "clear" ? 0 : elevation,
    $device: device
  }, props));
});
var sizeMap = {
  xlarge: 72,
  large: 64,
  medium: 56,
  small: 48,
  xsmall: 44
};
var iconSizeMap = {
  large: 24,
  small: 20
};

var StyledButton = _styled.button.withConfig({
  displayName: "DynamicIconButton__StyledButton",
  componentId: "rnknoi-0"
})(["", ";display:block;cursor:", ";user-select:none;width:", "px;height:", "px;transition:all 200ms cubic-bezier(0.5,0.5,0.5,1);"], foundationCss, function (p) {
  return p.disabled ? "initial" : "pointer";
}, function (p) {
  return p.width;
}, function (p) {
  return p.height;
});

var StyledSolidButton = _styled(StyledButton).withConfig({
  displayName: "DynamicIconButton__StyledSolidButton",
  componentId: "rnknoi-1"
})(["color:", ";"], function (p) {
  if (p.dark) {
    switch (p.appearance) {
      case "base":
        return p.disabled ? colors.whiteAlpha300 : colors.whiteAlpha500;

      case "colored":
        return p.disabled ? colors.whiteAlpha300 : colors.blackAlpha500;

      case "clear":
        return p.reactionType === "disabled" ? colors.whiteAlpha300 : p.reactionType === "normal" ? colors.whiteAlpha500 : colors.whiteAlpha700;
    }
  } else {
    switch (p.appearance) {
      case "base":
        return p.disabled ? colors.blackAlpha300 : colors.blackAlpha500;

      case "colored":
        return p.disabled ? colors.blackAlpha300 : colors.white;

      case "clear":
        return p.reactionType === "disabled" ? colors.blackAlpha300 : p.reactionType === "normal" ? colors.blackAlpha400 : colors.blackAlpha500;
    }
  }
});

var StyledIcon = _styled(DynamicIcon).withConfig({
  displayName: "DynamicIconButton__StyledIcon",
  componentId: "rnknoi-2"
})(["line-height:100%;height:100%;"]);
//# sourceMappingURL=DynamicIconButton.js.map